import {
  dateCompare,
  dateRangeToDateStringArray,
  dateStringRangeToDateStringArray,
  getDayOfWeekDaysForNext3Years,
  getMonthDaysForNext3Years,
  toPrecision
} from '@curbnturf/helpers';
import { DateTime } from 'luxon';
import { Status } from '../enums';
import { IAccessRoute, IListing, IListingTax, ISite, ISubPhoto, ITax } from '../interfaces';
import { AccessRoute } from './access-route';
import { Activity } from './activity';
import { Amenity } from './amenity';
import { LatLon } from './coordinates';
import { CoordinateGis } from './coordinates-gis';
import { Listing } from './listing';
import { Noise } from './noise';
import { Privacy } from './privacy';
import { Reservation } from './reservation';
import { RoadConditionDescribed } from './road-condition-described';
import { Tax } from './tax';
import { Vehicle } from './vehicle';

// @todo move these to some global constants
const FEEDBACK_INCENTIVE = 5;
const SERVICE_FEE_MINIMUM = 5;
const SERVICE_FEE_RATE = 0.15;

export class Site implements ISite {
  id?: number;
  syncId?: string;
  listing?: Listing;
  available?: boolean;
  coordinates?: CoordinateGis;
  name?: string;
  price?: number;
  privacy?: Privacy;
  noise?: Noise;
  blackoutDays?: string[]; // array of date strings '2018-12-25'
  blackoutMonths?: number[]; // array of months for which the property is unavailable (1 = January, 12 = December)
  blackoutDayOfWeek: string[];
  photos?: ISubPhoto[]; // array URL strings
  maxHeight?: number; // ft
  maxWidth?: number; // ft
  maxLength?: number; // ft
  description?: string;
  maxGuests?: number;
  tentOnly?: boolean;
  deleted?: number;
  vehicles?: Vehicle;
  created?: number;
  publishDate?: number;

  accessRoutes?: AccessRoute[];
  activities?: Activity[];
  amenities?: Amenity[];
  reservations?: Reservation[];

  modified?: number;

  get roadCondition(): RoadConditionDescribed {
    if (this.accessRoutes) {
      return Site.mergeRoadCondition(this.accessRoutes);
    }

    return new RoadConditionDescribed();
  }

  get nameId(): string {
    return `${this.name} (${this.id})`;
  }
  static mergeRoadCondition(accessRoutes: IAccessRoute[]): RoadConditionDescribed {
    return accessRoutes.reduce(
      (conditionArray: RoadConditionDescribed, route: IAccessRoute) => {
        if (route && route.roadCondition) {
          const roadCondition = route.roadCondition;
          for (const key in conditionArray) {
            if (Object.prototype.hasOwnProperty.call(conditionArray, key)) {
              if (roadCondition[key]) {
                conditionArray[key] = roadCondition[key];
              }
            }
          }
        }
        return conditionArray;
      },
      {
        paved: false,
        pavedDescription: '',
        smoothGravel: false,
        smoothGravelDescription: '',
        poorGravel: false,
        poorGravelDescription: '',
        highClearance: false,
        highClearanceDescription: '',
        offRoad: false,
        offRoadDescription: '',
      } as RoadConditionDescribed,
    );
  }

  constructor(data?: ISite) {
    if (data) {
      this.setData(data);
    }
  }

  setData(data: ISite): void {
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this as any)[key] = (data as any)[key];
      }
    }

    if (!this.blackoutDays) {
      this.blackoutDays = [];
    }

    if (!this.blackoutMonths) {
      this.blackoutMonths = [];
    } else {
      this.blackoutMonths = this.blackoutMonths.map((blackoutMonth) => {
        if (typeof blackoutMonth === 'string') {
          return parseInt(blackoutMonth, 10);
        }

        return blackoutMonth;
      });
    }

    if (!this.photos) {
      this.photos = [];
    }

    if (!this.amenities) {
      this.amenities = [];
    }

    if (!this.reservations) {
      this.reservations = [];
    }

    if (this.privacy) {
      this.privacy = new Privacy(this.privacy);
    }

    this.coordinates = new LatLon(this.coordinates);

    if (!this.activities) {
      this.activities = [];
    } else {
      this.activities = this.activities.map((activity) => new Activity(activity));
    }

    if (this.noise) {
      this.noise = new Noise(this.noise);
    }

    if (this.accessRoutes) {
      this.accessRoutes = this.accessRoutes.map((accessRoute) => new AccessRoute(accessRoute));
    } else {
      this.accessRoutes = [];
    }
  }

  getBusyDays(): string[] {
    if (!this.available) {
      return [];
    }

    return [...this.getBlackoutDays(), ...this.getReservationDays()].filter((days) => !!days).sort(dateCompare);
  }

  getBlackoutDays(): string[] {
    const blackoutDays: string[] = [];

    if (this.blackoutDays) {
      blackoutDays.push(
        ...this.blackoutDays.reduce((days: string[], blackout) => {
          days.push(...dateStringRangeToDateStringArray(blackout));

          return days;
        }, []),
      );
    }

    if (this.blackoutMonths) {
      this.blackoutMonths.forEach((month) => blackoutDays.push(...getMonthDaysForNext3Years(month)));
    }

    if (this.blackoutDayOfWeek) {
      this.blackoutDayOfWeek.forEach((day) => blackoutDays.push(...getDayOfWeekDaysForNext3Years(day)));
    }

    // A listing's blackout settings apply to every site under it.
    if (this.listing) {
      blackoutDays.push(...this.listing.getBlackoutDays());
    }

    return blackoutDays;
  }

  /** True if every night of [startIso, endIso) — checkout day excluded, matching
   *  booking convention — is free of blackout and reservation conflicts. Bounded
   *  range check (no 3-year day enumeration for the caller), for bulk availability
   *  filtering across many sites at once (e.g. date-filtered search). */
  isAvailableForRange(startIso: string, endIso: string): boolean {
    if (!this.available || !startIso || !endIso) {
      return false;
    }

    const start = DateTime.fromISO(startIso);
    const lastNight = DateTime.fromISO(endIso).minus({ days: 1 });

    if (!start.isValid || !lastNight.isValid || start > lastNight) {
      return true;
    }

    const busyDays = new Set(this.getBusyDays());
    return !dateRangeToDateStringArray(start, lastNight).some((night) => busyDays.has(night));
  }

  getReservationDays(): string[] {
    const reservationDays: string[] = [];

    if (this.reservations) {
      this.reservations.forEach((reservation) => {
        if (
          reservation.status &&
          reservation.status !== Status.rejected &&
          reservation.status !== Status.cancelled &&
          reservation.date
        ) {
          reservationDays.push(...reservation.date.toDateStringArray());
        }
      });
    }

    return reservationDays;
  }

  getPrice(): number {
    // Add CurbNTurf rate.
    const fee = Math.max(((this.price || 0) / (1 - SERVICE_FEE_RATE)) * SERVICE_FEE_RATE, SERVICE_FEE_MINIMUM);
    return toPrecision((this.price || 0) + fee + FEEDBACK_INCENTIVE, 2);
  }

  getPriceWithTax(listing?: IListing): number {
    let totalPrice = this.getPrice();

    // get taxes
    let totalTaxRate = 0;
    const listingTaxes: IListingTax[] | undefined = listing
      ? listing.listingTaxes
      : this.listing
        ? this.listing.listingTaxes
        : undefined;
    if (listingTaxes) {
      totalTaxRate = listingTaxes.reduce(
        (total: number, siteTax: IListingTax) =>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          total + parseFloat(siteTax.tax.rate as any) / 100,
        totalTaxRate,
      );
    }

    const taxes: ITax[] | undefined = listing ? listing.taxes : this.listing ? this.listing.taxes : undefined;
    if (taxes) {
      totalTaxRate = taxes.reduce((total: number, tax: ITax) => total + tax.rate / 100, totalTaxRate);
    }

    if (totalTaxRate) {
      totalPrice = totalPrice + totalTaxRate * totalPrice;
    }

    return totalPrice;
  }
}
